/**
 * Der Stand im Mail-Assistenten und wie er in eine Kampagne übersetzt wird.
 *
 * Alles, was der Assistent braucht, um eine Mail später wieder zu öffnen,
 * liegt in builder_data. Was die Redaktion und die Datenbank prüfen
 * (Wunschtermin, Eilt, Vorab-Antworten, Sportschulen), steht zusätzlich in
 * eigenen Spalten — dort greifen die Regeln aus Migration 0039.
 */
import type { AssistentEntwurf, Campaign, RequestAnswers, Segment } from '../../lib/supabase';
import { isSegmentUsable } from '../../lib/supabase';
import {
  datumLang, inhaltFehler, sichtbareFragen, stichtag, vorabBefund,
  type Antworten, type Daten, type Kontext, type Vorlage,
} from '../../lib/mailBuilder/vorlagen';
import { renderMail } from '../../lib/mailBuilder/render';
import { BACKOFFICE, SPORTSCHULEN, sportschule, sportschulenFuerStudios, type Sportschule } from '../../lib/sportschulen';
import { eiltGrundFehler, fruehesterVersandtag, isoTag } from '../../lib/freigabe';
import type { MailUser } from '../../types';

export const ABSENDER_NAME = import.meta.env.VITE_DEFAULT_SENDER_NAME || 'Randori-Pro';
export const ABSENDER_EMAIL = import.meta.env.VITE_DEFAULT_SENDER_EMAIL || 'no-reply@randori-pro.de';

export type SchrittId = 'vorlage' | 'fragen' | 'inhalt' | 'empfaenger' | 'pruefen';

export const SCHRITTE: { id: SchrittId; label: string }[] = [
  { id: 'vorlage', label: 'Vorlage' },
  { id: 'fragen', label: 'Vorab-Fragen' },
  { id: 'inhalt', label: 'Inhalt' },
  { id: 'empfaenger', label: 'Empfänger' },
  { id: 'pruefen', label: 'Prüfen & Einreichen' },
];

export interface Stand {
  vorlageId: string | null;
  /** Sportschulen (Anzeigenamen wie im CRM, z. B. „Wilmersdorf"). */
  schulen: string[];
  antworten: Antworten;
  daten: Daten;
  wunschDatum: string;
  wunschZeit: string;
  eilt: boolean;
  eiltGrund: string;
  /** Wohin Fragen gehen: Antworten-an und Hinweis unten in der Mail. */
  kontaktEmail: string;
  betreff: string;
  betreffManuell: boolean;
  vorschautext: string;
  vorschauManuell: boolean;
  segmentIds: string[];
  /** Wen die Mail erreichen soll, wenn keine Gruppe genau passt. */
  zielgruppe: string;
  /** Nachricht an die Redaktion beim Einreichen. */
  nachricht: string;
}

interface Bauplan {
  v: 1;
  daten: Daten;
  antworten: Antworten;
  schulen: string[];
  kontaktEmail: string;
  zielgruppe: string;
  nachricht: string;
  betreffManuell: boolean;
  vorschauManuell: boolean;
}

/** Die Sportschulen, für die jemand Mails vorbereiten darf. */
export function erlaubteSchulen(u: MailUser | null): Sportschule[] {
  if (!u) return [];
  if (u.role === 'studio_leiter') return sportschulenFuerStudios(u.studios);
  return SPORTSCHULEN;
}

export function leererStand(u: MailUser | null, vorlageId: string | null): Stand {
  const erlaubt = erlaubteSchulen(u);
  const schulen = erlaubt.length === 1 ? [erlaubt[0].name] : [];
  return {
    vorlageId,
    schulen,
    antworten: {},
    daten: {},
    wunschDatum: '',
    wunschZeit: '10:00',
    eilt: false,
    eiltGrund: '',
    kontaktEmail: standardKontakt(schulen),
    betreff: '',
    betreffManuell: false,
    vorschautext: '',
    vorschauManuell: false,
    segmentIds: [],
    zielgruppe: '',
    nachricht: '',
  };
}

function zweistellig(n: number): string {
  return String(n).padStart(2, '0');
}

export function standAusKampagne(c: Campaign): Stand {
  const b = (c.builder_data ?? {}) as Partial<Bauplan>;
  const wunsch = c.desired_send_at ? new Date(c.desired_send_at) : null;
  const schulen = Array.isArray(b.schulen) ? b.schulen : (c.target_studios ?? []);
  return {
    vorlageId: c.builder_template ?? null,
    schulen,
    antworten: b.antworten ?? {},
    daten: b.daten ?? {},
    wunschDatum: wunsch ? `${wunsch.getFullYear()}-${zweistellig(wunsch.getMonth() + 1)}-${zweistellig(wunsch.getDate())}` : '',
    wunschZeit: wunsch ? `${zweistellig(wunsch.getHours())}:${zweistellig(wunsch.getMinutes())}` : '10:00',
    eilt: !!c.is_urgent,
    eiltGrund: c.urgent_reason ?? '',
    kontaktEmail: b.kontaktEmail || c.reply_to || standardKontakt(schulen),
    betreff: c.original_subject || c.subject || '',
    betreffManuell: b.betreffManuell ?? !!c.subject,
    vorschautext: c.preview_text ?? '',
    vorschauManuell: b.vorschauManuell ?? !!c.preview_text,
    segmentIds: c.segment_ids ?? [],
    zielgruppe: b.zielgruppe ?? c.request_answers?.zielgruppe ?? '',
    nachricht: b.nachricht ?? '',
  };
}

export function schulenVon(s: Pick<Stand, 'schulen'>): Sportschule[] {
  return s.schulen.map((n) => sportschule(n)).filter((x): x is Sportschule => !!x);
}

export function ortText(s: Pick<Stand, 'schulen'>): string {
  const schulen = schulenVon(s);
  if (schulen.length === 0) return '';
  if (schulen.length === SPORTSCHULEN.length) return 'alle Sportschulen';
  return schulen.map((x) => x.name).join(', ');
}

export function standardKontakt(schulen: string[]): string {
  if (schulen.length === 1) return sportschule(schulen[0])?.email ?? BACKOFFICE.email;
  return BACKOFFICE.email;
}

/** Postfächer, an die Fragen gehen können. */
export function kontaktOptionen(s: Pick<Stand, 'schulen'>, u: MailUser | null): { email: string; label: string }[] {
  const out = schulenVon(s).map((x) => ({ email: x.email, label: `${x.name} (${x.email})` }));
  out.push({ email: BACKOFFICE.email, label: `Backoffice (${BACKOFFICE.email})` });
  const eigene = (u?.email ?? '').toLowerCase();
  if (/@randori-pro\.de$/.test(eigene) && !out.some((o) => o.email === eigene)) {
    out.push({ email: eigene, label: `Ich selbst (${eigene})` });
  }
  return out;
}

export function kontext(s: Stand): Kontext {
  return { schulen: schulenVon(s), kontaktEmail: s.kontaktEmail };
}

export function mailHtml(s: Stand, v: Vorlage): string {
  return renderMail(v.bauen(s.daten, kontext(s), s.antworten));
}

/** Wunschtermin als Zeitpunkt (Ortszeit), oder null. */
export function wunschZeitpunkt(s: Pick<Stand, 'wunschDatum' | 'wunschZeit'>): Date | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s.wunschDatum)) return null;
  const zeit = /^\d{2}:\d{2}$/.test(s.wunschZeit) ? s.wunschZeit : '10:00';
  const d = new Date(`${s.wunschDatum}T${zeit}:00`);
  return Number.isNaN(d.getTime()) ? null : d;
}

/**
 * Frühester Versandtag für eine Mail, die jetzt (bzw. zu `eingang`) bei der
 * Redaktion ist: ohne „eilt" vier Werktage später, mit „eilt" heute (bis
 * 10 Uhr) oder am nächsten Werktag.
 */
export function fruehesterTermin(eingang: Date = new Date(), eilt = false): string {
  return isoTag(fruehesterVersandtag(eingang, eilt));
}

/** Weniger Vorlauf als die vier Werktage der Redaktion? */
export function istKnapp(s: Pick<Stand, 'wunschDatum' | 'wunschZeit'>, eingang: Date = new Date()): boolean {
  const w = wunschZeitpunkt(s);
  return !!w && isoTag(w) < fruehesterTermin(eingang, false);
}

/** Auch mit „eilt" zu früh (nach 10 Uhr für heute, am Wochenende …)? */
export function eiltZuFrueh(s: Pick<Stand, 'wunschDatum' | 'wunschZeit'>, eingang: Date = new Date()): boolean {
  const w = wunschZeitpunkt(s);
  return !!w && isoTag(w) < fruehesterTermin(eingang, true);
}

/** Wie die Vorlauf-Regeln greifen: für wen (selbst versenden?) und ab wann. */
export interface VorlaufKontext {
  /** Darf ohne Freigabe versenden — dann gilt der Vorlauf der Redaktion nicht. */
  selbst: boolean;
  /** Eingang bei der Redaktion; nach einer Rückfrage das erste Einreichen der Runde. */
  eingang: Date;
}

export function requestAnswers(s: Stand, v: Vorlage): RequestAnswers {
  const befund = vorabBefund(v, s.antworten);
  return {
    vorlage: v.name,
    antworten: sichtbareFragen(v, s.antworten).map((f) => {
      const o = f.optionen.find((x) => x.wert === s.antworten[f.id]);
      return { id: f.id, frage: f.frage, antwort: o?.label ?? '—', hinweis: o?.folge === 'hinweis' ? o.text : undefined };
    }),
    offene_punkte: befund.klaeren,
    zielgruppe: s.zielgruppe.trim() || undefined,
  };
}

export function kampagnenName(s: Stand, v: Vorlage): string {
  const ort = ortText(s);
  const wann = s.wunschDatum ? s.wunschDatum.split('-').reverse().slice(0, 2).join('.') + '.' : '';
  return [v.name, ort, wann].filter(Boolean).join(' · ');
}

export function alsEntwurf(s: Stand, v: Vorlage): AssistentEntwurf {
  const w = wunschZeitpunkt(s);
  const bauplan: Bauplan = {
    v: 1,
    daten: s.daten,
    antworten: s.antworten,
    schulen: s.schulen,
    kontaktEmail: s.kontaktEmail,
    zielgruppe: s.zielgruppe,
    nachricht: s.nachricht,
    betreffManuell: s.betreffManuell,
    vorschauManuell: s.vorschauManuell,
  };
  return {
    name: kampagnenName(s, v),
    subject: s.betreff.trim(),
    preview_text: s.vorschautext.trim() || null,
    sender_name: ABSENDER_NAME,
    sender_email: ABSENDER_EMAIL,
    reply_to: s.kontaktEmail || null,
    html_content: mailHtml(s, v),
    segment_ids: s.segmentIds,
    builder_template: v.id,
    builder_data: bauplan as unknown as Record<string, unknown>,
    request_answers: requestAnswers(s, v),
    desired_send_at: w ? w.toISOString() : null,
    is_urgent: s.eilt,
    urgent_reason: s.eilt ? s.eiltGrund.replace(/\s+/g, ' ').trim() || null : null,
    target_studios: s.schulen,
  };
}

// ── Prüfungen je Schritt ─────────────────────────────────────────────────────

export function fragenFehler(s: Stand, v: Vorlage, vk: VorlaufKontext = { selbst: false, eingang: new Date() }): string[] {
  const out: string[] = [];
  if (s.schulen.length === 0) out.push('Wähl, für welche Sportschule die Mail ist.');
  for (const f of sichtbareFragen(v, s.antworten)) {
    if (!f.optionen.some((o) => o.wert === s.antworten[f.id])) out.push(`Bitte beantworte: „${f.frage}"`);
  }
  const w = wunschZeitpunkt(s);
  if (!w) out.push('Trag einen Wunschtermin für den Versand ein.');
  else if (w.getTime() < Date.now()) out.push('Der Wunschtermin liegt in der Vergangenheit.');
  if (w && !vk.selbst && w.getTime() >= Date.now()) {
    if (!s.eilt && istKnapp(s, vk.eingang)) {
      out.push(`Die Redaktion braucht vier Werktage — frühester Termin ohne „eilt" ist ${datumLang(fruehesterTermin(vk.eingang))}. Wähl einen späteren Tag oder markier die Mail als „eilt".`);
    }
    if (s.eilt && eiltZuFrueh(s, vk.eingang)) {
      out.push(`Auch mit „eilt" geht die Mail frühestens am ${datumLang(fruehesterTermin(vk.eingang, true))} raus: Was bis 10 Uhr bei der Redaktion ist, geht am selben Tag raus, sonst am nächsten Werktag.`);
    }
  }
  if (!vk.selbst && s.eilt) {
    const g = eiltGrundFehler(s.eiltGrund);
    if (g) out.push(g);
  }
  if (!/^[a-z0-9._%+-]+@randori-pro\.de$/i.test(s.kontaktEmail.trim())) out.push('Wähl, wohin Fragen zur Mail gehen sollen.');
  return out;
}

export function inhaltsFehler(s: Stand, v: Vorlage): string[] {
  const out = inhaltFehler(v, s.daten, s.antworten);
  const b = s.betreff.trim();
  if (!b) out.push('Der Betreff fehlt.');
  else if (b.length > 120) out.push('Der Betreff ist zu lang (höchstens 120 Zeichen).');
  const st = stichtag(v, s.daten);
  if (st && s.wunschDatum && s.wunschDatum > st.datum) {
    out.push(`Der Wunschtermin (${datumLang(s.wunschDatum)}) liegt nach ${st.was} (${datumLang(st.datum)}) — dann kommt die Mail zu spät. Leg den Wunschtermin in den Vorab-Fragen früher.`);
  }
  return out;
}

export function empfaengerFehler(s: Stand, gruppen: Segment[]): string[] {
  const out: string[] = [];
  const gewaehlt = gruppen.filter((g) => s.segmentIds.includes(g.id));
  if (gewaehlt.length === 0) out.push('Wähl mindestens eine Empfängergruppe.');
  if (gewaehlt.some((g) => !isSegmentUsable(g))) out.push('Eine gewählte Gruppe ist abgelaufen — bitte abwählen.');
  return out;
}

/** Hinweise, die nicht aufhalten, aber auffallen sollen. */
export function stichtagHinweis(s: Stand, v: Vorlage): string | null {
  const st = stichtag(v, s.daten);
  if (st && s.wunschDatum && s.wunschDatum === st.datum) {
    return `Die Mail geht am selben Tag raus wie ${st.was}. Reicht das den Empfänger:innen?`;
  }
  return null;
}

// ── Empfängergruppen ────────────────────────────────────────────────────────

export type GruppenAbschnitt = { id: 'schule' | 'tags' | 'alle'; titel: string; text: string; gruppen: Segment[] };

/**
 * Welche Gruppen passen zur gewählten Sportschule? Die Datenbank zeigt der
 * Studioleitung ohnehin nur ihre eigenen; hier wird nur sortiert, damit die
 * passende Gruppe oben steht.
 */
export function gruppenFuer(stand: Pick<Stand, 'schulen'>, user: MailUser | null, alle: Segment[]): GruppenAbschnitt[] {
  const orgs = schulenVon(stand).flatMap((s) => s.orgs);
  const waehlbar = alle.filter((g) => !g.always_included && !g.admin_only && isSegmentUsable(g));
  const zurSchule = (g: Segment) => !!g.studio_scope?.some((o) => orgs.includes(o));
  const istTag = (g: Segment) => !!g.crm_tag_slug;
  const uebergreifend = user?.role !== 'studio_leiter';

  const eigene = waehlbar.filter((g) => !istTag(g) && zurSchule(g));
  const tags = waehlbar.filter((g) => istTag(g) && (zurSchule(g) || (uebergreifend && !g.studio_scope)));
  const alleSchulen = uebergreifend ? waehlbar.filter((g) => !istTag(g) && !g.studio_scope) : [];

  const nachName = (a: Segment, b: Segment) => a.name.localeCompare(b.name, 'de');
  const out: GruppenAbschnitt[] = [
    { id: 'schule', titel: `Gruppen für ${ortText(stand) || 'die Sportschule'}`, text: 'Mitglieder und Familien der Sportschule — nach Zahlstatus und Alter.', gruppen: eigene.sort(nachName) },
    { id: 'tags', titel: 'Personen mit CRM-Tag (Tag-Mails)', text: 'Wer im CRM den Tag trägt, ist drin — stündlich aktualisiert. Fehlt ein Tag? Im CRM vergeben, spätestens nach einer Stunde ist die Gruppe hier.', gruppen: tags.sort(nachName) },
    { id: 'alle', titel: 'Sportschulübergreifend', text: 'Gruppen über alle Sportschulen. Nur nehmen, wenn die Mail wirklich alle betrifft.', gruppen: alleSchulen.sort(nachName) },
  ];
  return out.filter((a) => a.gruppen.length > 0);
}
